import { LitElement, html, css } from 'lit';
import { getImageSrc, getProductImage } from '../../../utils/utils';

class OfferDialog extends LitElement {
  static properties = {
    type: { type: String }, // 'edit' or 'counter'
    productImage: { type: String },
    productTitle: { type: String },
    productPrice: { type: String },
    currentOfferPrice: { type: String },
    onConfirm: { type: Function },
    onCancel: { type: Function },
  };

  constructor() {
    super();
    this.type = 'edit';
    this.productImage = '';
    this.productTitle = '';
    this.productPrice = '';
    this.currentOfferPrice = '';
    this.inputPrice = '';
  }

  static styles = css`
    .offer-dialog {
      background: var(--bg-primary-color, #ffffff);
      border-radius: 12px;
      padding: 24px;
      max-width: 450px !important;
      width: 90%;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 500px;
    }

    .dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
    }

    .dialog-title {
      font-size: 20px;
      font-weight: 600;
      color: var(--text-primary-color, #1a1a1a);
      margin: 0;
    }

    .close-button {
      background: none;
      border: none;
      font-size: 24px;
      cursor: pointer;
      color: var(--text-secondary-color, #6b7280);
      padding: 4px;
      border-radius: 50%;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background-color 0.2s;
    }

    .close-button:hover {
      background-color: var(--bg-quinary-color, #f3f4f6);
    }

    .product-info {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 20px;
      padding: 12px;
      border-radius: 8px;
      border: 1px solid var(--border-color);
    }

    .product-image {
      width: 60px;
      height: 60px;
      border-radius: 8px;
      object-fit: cover;
      background: var(--bg-secondary-color, #e5e7eb);
    }

    .product-details {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }

    .product-title {
      font-size: 16px;
      font-weight: 600;
      color: var(--text-primary-color, #1a1a1a);
      margin: 0 0 4px 0;
    }

    .product-price {
      font-size: 14px;
      color: var(--text-secondary-color, #6b7280);
      margin: 0;
    }

    .price-input-section {
      margin-bottom: 20px;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .price-label {
      display: block;
      font-size: 14px;
      font-weight: 500;
      color: var(--text-primary-color, #1a1a1a);
      margin-bottom: 8px;
    }

    .price-input {
      width: 100%;
      padding: 12px;
      border: 1px solid var(--border-color, #e1e5e9);
      border-radius: 8px;
      font-size: 16px;
      color: var(--text-primary-color, #1a1a1a);
      background: var(--bg-primary-color, #ffffff);
      transition: border-color 0.2s;
      box-sizing: border-box;
    }

    .price-input:focus {
      outline: none;
      border-color: var(--text-accent, #241172);
      box-shadow: 0 0 0 3px rgba(109, 62, 193, 0.1);
    }

    .terms-text {
      font-size: 12px;
      color: var(--text-secondary-color, #6b7280);
      margin-bottom: 20px;
      line-height: 1.4;
    }

    .terms-link {
      color: var(--text-accent, #241172);
      text-decoration: none;
      font-weight: 500;
    }

    .terms-link:hover {
      text-decoration: underline;
    }

    .submit-button {
      width: 100%;
      padding: 14px;
      background: var(--text-accent, #241172);
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      transition: background-color 0.2s;
    }

    .submit-button:hover {
      background: var(--text-accent-hover, #5a2fa0);
    }

    .submit-button:disabled {
      background: var(--bg-tertiary-color, #d1d5db);
      cursor: not-allowed;
    }

    @media (max-width: 768px) {
      .offer-dialog {
        border-radius: 16px 16px 0 0;
        margin: 0;
        max-width: none;
        width: auto;
      }
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.inputPrice = this.currentOfferPrice;
  }

  handleInputChange(e) {
    this.inputPrice = e.target.value;
    this.requestUpdate();
  }

  handleSubmit() {
    if (this.onConfirm) {
      this.onConfirm(this.inputPrice);
    }
  }

  handleCancel() {
    if (this.onCancel) {
      this.onCancel();
    }
  }

  getDialogTitle() {
    return this.type === 'edit' ? 'Edit Offer' : 'Counter Offer';
  }

  getSubmitButtonText() {
    return this.type === 'edit' ? 'Submit' : 'Submit Counter Offer';
  }

  render() {
    return html`
      <div class="offer-dialog">
      <div class="dialog-content">
        <div class="dialog-header">
          <h3 class="dialog-title">${this.getDialogTitle()}</h3>
          <button class="close-button" @click=${this.handleCancel}>×</button>
        </div>

        <div class="product-info">
          <img 
            src="${getImageSrc(getProductImage(this.productImage))}" 
            alt="${this.productTitle}"
            class="product-image"
            @error=${(e) => {
              e.target.style.display = 'none';
            }}
          />
          <div class="product-details">
            <h4 class="product-title">${this.productTitle}</h4>
            <p class="product-price">${this.productPrice}</p>
          </div>
        </div>

        <div class="price-input-section">
          <label class="price-label">Price</label>
          <input
            type="text"
            class="price-input"
            .value=${this.inputPrice}
            @input=${this.handleInputChange}
            placeholder="Enter offer price"
          />
        </div>
        </div>

        <div class="dialog-footer">
               <p class="terms-text">
          By submitting you agree to Merkaru's 
          <a href="/terms-of-service" class="terms-link">Terms & Conditions</a>
        </p>

        <button 
          class="submit-button"
          @click=${this.handleSubmit}
          ?disabled=${!this.inputPrice.trim()}
        >
          ${this.getSubmitButtonText()}
        </button>
        </div>
    
      </div>
    `;
  }
}

customElements.define('offer-dialog', OfferDialog); 